"use client"

import React from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import ImageWithFallback from "@/components/ui/image-with-fallback"
import ProfileContactSection from "@/components/profile/profile-contact-section"

// Minimal profile shape to support both PublicProfile and ProfileSummary inputs
type ListCardProfile = {
  id: string
  name: string
  title?: string
  image?: string | null
  imageUrl?: string | null
  bio?: string
  experience?: string
  location?: string
  boardSeats?: number
  previousCompanies?: string[]
  sectors?: string[]
}

export interface ProfileListCardProps {
  profile: ListCardProfile
  onView: (profile: ListCardProfile) => void
  className?: string
}

function getInitials(name?: string) {
  if (!name) return "EP"
  const parts = name.trim().split(/\s+/)
  const initials = parts.slice(0, 2).map(p => p[0]?.toUpperCase() || "").join("")
  return initials || "EP"
}

export default function ProfileListCard({ profile, onView, className }: ProfileListCardProps) {
  const handleView = () => {
    onView(profile)
  }

  return (
    <div
      onClick={handleView}
      className={cn(
        "group bg-white border border-gray-200 rounded-3xl shadow-soft-md hover:shadow-soft-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden hover-lift cursor-pointer",
        className
      )}
      role="article"
      aria-label={`${profile.name} – ${profile.title || "Executive"}`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleView()
        }
      }}
    >
      <div className="flex flex-col sm:flex-row gap-6 p-8">
        {/* Profile Photo - Left Side */}
        <div className="flex-shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleView()
            }}
            title={`View ${profile.name}`}
            aria-label={`View ${profile.name} profile`}
            className="group/image w-32 h-32 sm:w-40 sm:h-40 bg-slate-100 rounded-xl overflow-hidden relative flex items-center justify-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#5a82bd] transition-smooth hover:scale-105"
          >
            <ImageWithFallback
              imageUrl={(profile.image ?? profile.imageUrl ?? null) || undefined}
              name={profile.name}
              alt={`${profile.name} headshot`}
              className="absolute inset-0 w-full h-full"
              size={0}
              sizes="160px"
              fit="cover"
              objectPositionClass="object-center"
              imgClassName="w-full h-full object-cover"
              rounded="none"
            />
          </button>
        </div>

        {/* Content - Right Side */}
        <div className="flex-1 min-w-0">
          {/* Header with Name and Title */}
          <div className="mb-3">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 uppercase tracking-wide mb-1">
              {profile.name}
            </h3>
            {profile.title && (
              <p className="text-base sm:text-lg text-[#6b9bd2] font-medium">
                {profile.title}
              </p>
            )}
          </div>

          {/* Quick Stats Bar */}
          <div className="flex flex-wrap gap-4 sm:gap-5 mb-5 text-sm text-gray-600">
            {profile.experience && (
              <div className="flex items-center gap-1.5">
                <span className="text-[#6b9bd2] font-medium">Experience:</span>
                <span className="font-medium">{profile.experience}</span>
              </div>
            )}

            {profile.previousCompanies && profile.previousCompanies.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-[#6b9bd2] font-medium">Previously:</span>
                <span className="font-medium">{profile.previousCompanies[0]}</span>
              </div>
            )}

            {profile.boardSeats && profile.boardSeats > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-[#6b9bd2] font-medium">Board Seats:</span>
                <span className="font-medium">{profile.boardSeats}</span>
              </div>
            )}

            {profile.location && (
              <div className="flex items-center gap-1.5">
                <span className="text-[#6b9bd2] font-medium">Location:</span>
                <span className="font-medium">{profile.location}</span>
              </div>
            )}
          </div>

          {/* Bio/Description - Full Text */}
          {profile.bio && (
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-5 line-clamp-4">
              {profile.bio}
            </p>
          )}

          {/* Sectors/Tags */}
          {profile.sectors && profile.sectors.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {profile.sectors.slice(0, 4).map((sector, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                >
                  {sector}
                </span>
              ))}
              {profile.sectors.length > 4 && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600">
                  +{profile.sectors.length - 4} more
                </span>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mt-5">
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleView()
              }}
              size="sm"
              variant="outline"
              className={cn(
                "rounded-full tracking-wide text-[#6b9bd2] border-[#6b9bd2] hover:bg-[#6b9bd2] hover:text-white transition-smooth hover:scale-105",
                "py-2 px-6 text-sm font-medium"
              )}
              aria-label={`View full profile for ${profile.name}`}
            >
              VIEW PROFILE
            </Button>

            <div onClick={(e) => e.stopPropagation()}>
              <ProfileContactSection
                profileId={profile.id}
                profileName={profile.name}
                className={cn(
                  "rounded-full tracking-wide text-white transition-smooth hover:scale-105",
                  "bg-[#6b9bd2] hover:bg-[#5a82bd] shadow-soft hover:shadow-warm-glow",
                  "py-2 px-6 text-sm font-medium uppercase"
                )}
              >
                Contact Now
              </ProfileContactSection>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
